import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useState } from 'react'
import { ChargingSchedule } from '../charging-schedule'
import VehicleDetailsCard from './VehicleDetailsCard'
import StateOfChargeCard from './StateOfChargeCard'
import { Button, Stack, Typography } from '@mui/material'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { Box, useMediaQuery, useTheme } from '@mui/system'
import { ChargingStateType, ManufacturerState, VehicleChargingState } from '../../types'
import NotificationBanner from 'src/Components/banner/NotificationBanner'
import { J2Multiply } from '@e3dc-react/icons'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'
import { getValueForVehicleDetailCard } from '../../functions'
import RemoveVehicleModal from '../RemoveVehicleModal'
import { makeStyles } from 'tss-react/mui'
import { UserActionRequiredErrorDto } from '../../dto/vehicles.dto'
import { UserActionRequiredTile } from '../vehicle-tile'
import { VehicleSettingsModal } from '../vehicle-settings'
import { WarningTile } from '../messages'

const useStyles = makeStyles()(theme => ({
  dialogTitle: {
    padding: theme.spacing(2, 2, 4, 2),

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 4, 2, 4),
    },
  },
  title: {
    color: theme.palette.text.primary,
    margin: 0,
    padding: 0,
    maxWidth: '80%',
  },
  closeButton: {
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.text.primary,
    [theme.breakpoints.up('md')]: {
      right: theme.spacing(3),
      top: theme.spacing(3),
    },
  },

  editButton: {
    right: theme.spacing(7),
    top: theme.spacing(1),
    color: theme.palette.text.primary,
    [theme.breakpoints.up('md')]: {
      right: theme.spacing(9),
      top: theme.spacing(3),
    },
  },
}))

export interface VehicleDetailContainerModalProps {
  onClose?: () => void
  place?: number
  installationId?: string
  vehicleName: string
  vehicleId: number
  estimatedRange?: number
  currentSoc?: number
  targetStateOfCharge?: number
  status?: ChargingStateType
  imageUrl: string | null
  hasCustomImage: boolean
  vin?: string
  sunmodeActive: boolean
  vendorState?: ManufacturerState
  vehicleAvailable: boolean
  wallboxIndex?: number
  wallboxChargingStatus?: ChargingStatus
  open: boolean
  chargeLimitMax?: number
  userActionRequiredError: UserActionRequiredErrorDto[] | null
}

/***
 * VehicleDetailContainerModal shows charging details of current user vehicle.
 * It's acontainer for UI and data handling of vehicle details, state of charge, and charging schedule.
 */
const VehicleDetailContainerModal: React.FC<VehicleDetailContainerModalProps> = ({
  onClose,
  installationId,
  vehicleName,
  vehicleId,
  estimatedRange,
  currentSoc,
  targetStateOfCharge,
  status,
  sunmodeActive,
  vendorState,
  vehicleAvailable,
  imageUrl,
  hasCustomImage,
  vin,
  wallboxIndex,
  wallboxChargingStatus,
  open,
  chargeLimitMax,
  userActionRequiredError,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const [isRemoveVehicleModalOpen, setIsRemoveVehicleModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const { classes } = useStyles()

  const hasUserActionRequiredError = userActionRequiredError && userActionRequiredError.length > 0
  const hasChargingError = status === VehicleChargingState.fault
  const showWarning = hasUserActionRequiredError || hasChargingError

  const onEdit = (): void => {
    setIsEditMode(true)
  }

  return (
    <>
      <Dialog
        onClose={() => onClose?.()}
        maxWidth={'sm'}
        open={open}
        title={vehicleName}
        fullScreen={!upMd}
        titleClassName={classes.title}
        dialogTitleClassName={classes.dialogTitle}
        closeButtonClassName={classes.closeButton}
        editButtonClassName={classes.editButton}
        editButtonVisible={true}
        onEdit={onEdit}
      >
        {showWarning && (
          <Stack mt={2} mb={4} gap={2}>
            {hasUserActionRequiredError && <UserActionRequiredTile userActionRequiredError={userActionRequiredError} showAllErrors={true} />}
            {hasChargingError && (
              <WarningTile
                message={t('vehicle-integration.warning-message.plug-in-vehicle-again')}
                iconSx={{ color: 'error.dark' }}
                containerSx={{ backgroundColor: 'error.light' }}
              />
            )}
          </Stack>
        )}

        <VehicleDetailsCard
          imageUrl={imageUrl}
          range={getValueForVehicleDetailCard(estimatedRange)}
          currentSoc={getValueForVehicleDetailCard(currentSoc)}
          targetStateOfCharge={getValueForVehicleDetailCard(targetStateOfCharge)}
          status={status}
          wallboxIndex={wallboxIndex}
          wallboxChargingStatus={wallboxChargingStatus}
          vehicleAvailable={vehicleAvailable}
          chargeLimitMax={chargeLimitMax}
        />
        <Box>
          {vehicleAvailable ? (
            <>
              <StateOfChargeCard vehicleId={vehicleId.toString()} installationId={installationId} chargeLimitMax={chargeLimitMax} />
              <ChargingSchedule
                vehicleId={vehicleId.toString()}
                installationId={installationId}
                sunmodeActive={sunmodeActive}
                chargeLimitMax={chargeLimitMax}
              />
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: theme.spacing(2) }}>
                <Typography variant="body2">{t('emobility.vehicle-integration.vehicle-dialog.vehicle-not-available')}</Typography>
                <J2Multiply />
              </Box>
              <Box sx={{ py: 4 }}>
                <NotificationBanner
                  variant={vendorState === ManufacturerState.unavailable ? 'warning' : 'info'}
                  text={
                    vendorState === ManufacturerState.unavailable
                      ? t('emobility.vehicle-integration.vehicle-dialog.vendor-unavailable')
                      : t('emobility.vehicle-integration.vehicle-dialog.vendor-temporarily-unavailable')
                  }
                />
              </Box>
            </>
          )}
          <Button sx={{ mt: 2, mx: 'auto', display: 'block' }} variant="text" onClick={() => setIsRemoveVehicleModalOpen(true)}>
            {t('emobility.vehicle-integration.charging-schedule.delete-vehicle')}
          </Button>
        </Box>
      </Dialog>
      {isRemoveVehicleModalOpen && (
        <RemoveVehicleModal
          title={t('emobility.vehicle-integration.disconnect-vehicle-dialog.title')}
          description={'vehicle-integration.disconnect-vehicle-dialog.text'}
          installationId={Number(installationId)}
          vehicleId={vehicleId}
          open={isRemoveVehicleModalOpen}
          onClose={() => setIsRemoveVehicleModalOpen(false)}
        />
      )}
      {isEditMode && (
        <VehicleSettingsModal
          installationId={Number(installationId)}
          vehicleId={vehicleId}
          isOpen={isEditMode}
          onClose={() => setIsEditMode(false)}
          imageUrl={imageUrl}
          hasCustomImage={hasCustomImage}
          vin={vin}
          vehicleName={vehicleName}
        />
      )}
    </>
  )
}

export default VehicleDetailContainerModal
